import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config";
import { fetchRouteRegistry } from "../../Utils/fetchRouteRegistry";

const today = () => new Date().toISOString().slice(0, 10);
const number = value => Number(value) || 0;
const formatTime = timestamp => {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};
const formatPair = (left, right) => (number(left) > 0 || number(right) > 0) ? `${number(left)} / ${number(right)}` : "-";
const messageStyle = msg => (msg.includes("สำเร็จ") || msg.includes("แล้ว")) ? success : error;

const TABS = [
  { key: "loadOut", label: "ขึ้นน้ำแข็ง" },
  { key: "refill", label: "เพิ่มน้ำแข็ง" },
  { key: "return", label: "กระสอบกลับ" },
  { key: "summary", label: "สรุป" },
];

const routeValue = option => option.nameuser || option.username;
// The dropdown shows (and the record stores) the route's display name, but sales data is keyed
// by the seller's auth uid. Sending the uid alongside the name is what lets the ledger summary
// join to real sales — without it "ขาย" and "ขาด/เกิน" read 0 on every route (bug found
// 2026-09-14). Returns undefined for a route typed in by hand that matches no known user; the
// backend then falls back to the route's most recent row that does carry a uid.
const uidForRoute = (options, route) => {
  const match = (options || []).find(option => routeValue(option) === route);
  return match ? match.uid : undefined;
};

// Shared between LoadOutTab (an all-routes preview alongside the sub-tab entry form) and
// SummaryTab (its main table) — one row per vehicle/route for one businessDate. Each ต๊ง gets its
// own row with ลำดับ/ทะเบียนรถ/สาย spanning the vehicle's whole group via rowSpan (2026-08-28 —
// Vector: unlimited T columns forced horizontal scroll; unlimited rows scrolling vertically
// instead is the fix), closed out by a "รวม" row per vehicle. A trip with a real record id is
// editable via onEditRecord(recordId).
//
// T only counts ขึ้นกระสอบ (trip.source === "LOAD_OUT") — Vector, 2026-09-22: 2 ขึ้นกระสอบ + N
// เพิ่มน้ำแข็ง used to print T.1..T.(2+N) off the merged array's plain index, which is wrong; a
// เพิ่มน้ำแข็ง row must show the word "เพิ่มน้ำแข็ง" and never advance the T counter.
const tLabel = (trip, loadOutIndex) => trip.source === "REFILL" ? "เพิ่มน้ำแข็ง" : `T.${loadOutIndex}`;

export const VehicleReportTable = ({ rows, onEditRecord }) => {
  return <div style={tableWrap}><table style={ledgerTable}><thead><tr>
    <th style={rowHeader}>ลำดับ</th><th style={rowHeader}>ทะเบียนรถ</th><th style={rowHeader}>สาย</th>
    <th style={numberHeader}>T</th><th style={numberHeader}>หลอดใหญ่</th><th style={numberHeader}>บด</th><th style={numberHeader}>แก้ไข</th>
  </tr></thead><tbody>
    {rows.map(row => {
      const trips = row.trips || [];
      if (!trips.length) {
        return <tr key={row.plate}>
          <td style={rowHeader}>{row.order}</td>
          <td style={rowHeader}>{row.plate}</td>
          <td style={rowHeader}>{row.route || "-"}</td>
          <td style={sumCell}>-</td>
          <td style={sumCell}>-</td>
          <td style={sumCell}>-</td>
          <td style={sumCell}>-</td>
        </tr>;
      }
      let loadOutCount = 0;
      return <React.Fragment key={row.plate}>
        {trips.map((trip, i) => {
          if (trip.source === "LOAD_OUT") loadOutCount += 1;
          return <tr key={trip.id ?? i}>
          {i === 0 && <>
            <td style={rowHeader} rowSpan={trips.length + 1}>{row.order}</td>
            <td style={rowHeader} rowSpan={trips.length + 1}>{row.plate}</td>
            <td style={rowHeader} rowSpan={trips.length + 1}>{row.route || "-"}</td>
          </>}
          <td style={sumCell}>{tLabel(trip, loadOutCount)}</td>
          <td style={sumCell}>{number(trip.tubeQty)}</td>
          <td style={sumCell}>{number(trip.crushedQty)}</td>
          <td style={sumCell}>{trip.id != null && onEditRecord
            ? <button type="button" style={tCellButton} onClick={() => onEditRecord(trip.id)} title="แก้ไขต๊งนี้">แก้ไข</button>
            : "-"}</td>
        </tr>;
        })}
        <tr style={calculatedRow}>
          <th style={rowHeader}>รวม</th>
          <td style={sumCell}>{row.totalTube != null ? row.totalTube : "-"}</td>
          <td style={sumCell}>{row.totalCrushed != null ? row.totalCrushed : "-"}</td>
          <td style={sumCell}>-</td>
        </tr>
      </React.Fragment>;
    })}
    {!rows.length && <tr><td colSpan={7} style={emptyRow}>ยังไม่มีข้อมูล</td></tr>}
  </tbody></table></div>;
};

// Locked entry template, "แบบหน้า SCADA" (Vector, 2026-09-28: "หน้าขึ้นน้ำแข็ง ควรเป็น templete
// ล็อคตายเลย แล้วค่า lock ตายหมด เป็น base แล้ว ให้ แก้เลขแล้วกด post เอา"). Grid rows now come
// from the server-side template (GET .../bag-load-out/template — one set factory-wide, edited
// only via "แก้แม่แบบ", report:write), NOT from the all-plates roster GET vehicle-report used
// elsewhere on this page. ลำดับ/ทะเบียนรถ/สาย are locked plain text in normal mode; each T cell
// opens pre-filled with the template's base value and is edited in place, same text-input format
// as before ("<หลอด>-<บด>", parsed on blur). No more "ยกยอด" (roll-forward) anywhere — the
// template's base values ARE what used to be carried forward daily.
//
// The form is a PLAN: it always opens from the template (plus, if the clerk already edited today's
// plan on this browser, those edits from localStorage — see planStorageKey). It never loads the
// day's real bag_load_out_records (IMD's real loads), so nothing typed here can overwrite them
// (Vector 2026-09-30: "ลงไว้แม่แบบ ส่วน imd ส่งมาเป็นของจริง ไม่เกี่ยวกับแม่แบบเลย").
const CELL_PATTERN = /^(\d+)(?:\s*-\s*(\d+))?$/;
// undefined = blank (no ต๊ง, not an error) · null = text present but doesn't match the pattern
// (error) · object = parsed ok.
const parseCellText = text => {
  const trimmed = (text || "").trim();
  if (!trimmed) return undefined;
  const match = CELL_PATTERN.exec(trimmed);
  if (!match) return null;
  return { tubeQty: Number(match[1]), crushedQty: match[2] !== undefined ? Number(match[2]) : 0 };
};
const formatCellText = (tubeQty, crushedQty) => number(crushedQty) > 0 ? `${number(tubeQty)}-${number(crushedQty)}` : `${number(tubeQty)}`;

const rowKeyOf = (route, plate) => `${(route || "").trim()}||${(plate || "").trim()}`;

const planStorageKey = businessDate => `loadout-plan:${businessDate}`;

const readSavedPlan = businessDate => {
  try {
    const parsed = JSON.parse(localStorage.getItem(planStorageKey(businessDate)) || "null");
    return Array.isArray(parsed?.rows) ? parsed.rows : [];
  } catch (_) { return []; }
};

const writeSavedPlan = (businessDate, gridRows) => {
  try {
    localStorage.setItem(planStorageKey(businessDate), JSON.stringify({
      rows: gridRows.map(row => {
        const base = { route: row.route, plate: row.plate, driverName: row.driverName || "", cells: row.cells.map(cell => ({ text: cell.text, note: cell.note })) };
        return row.isOrphan
          ? { ...base, orphan: true, include: !!row.includeInLineImage }
          : { ...base, tplRoute: row.tplRoute, tplPlate: row.tplPlate };
      }),
    }));
  } catch (_) { /* storage full/blocked — the plan just isn't remembered */ }
};

let orphanSeq = 0;
const savedCellsToGrid = (savedRow, idPrefix) => (savedRow.cells || []).map((c, i) => ({ cellId: `${idPrefix}-${i}`, text: c.text || "", note: c.note || "", error: "" }));

// Each template row shows the template's base trips, unless the day's saved plan has an entry for
// the same template row (matched by the template's own route+plate, so a plate/route the clerk
// edited for today still finds its saved cells) — then the saved values win (template edits made
// since then don't silently re-shape a plan the clerk already started). Saved off-template rows
// ("orphan": added by the clerk for this day only, never part of the template) follow the template
// rows and are opted into the LINE image only when ticked.
const buildGridRows = (templateRows, savedPlanRows = []) => {
  const savedTemplateRows = savedPlanRows.filter(saved => !saved.orphan);
  const used = new Set();
  const templateBased = templateRows.map((row, rowIndex) => {
    const tplKey = rowKeyOf(row.route, row.vehiclePlate);
    const savedIndex = savedTemplateRows.findIndex((saved, i) => !used.has(i) && rowKeyOf(saved.tplRoute ?? saved.route, saved.tplPlate ?? saved.plate) === tplKey);
    const savedRow = savedIndex >= 0 ? savedTemplateRows[savedIndex] : null;
    if (savedRow) used.add(savedIndex);
    const cells = savedRow
      ? savedCellsToGrid(savedRow, `plan-${row.id ?? rowIndex}`)
      : (row.trips || []).map((base, i) => ({ cellId: `base-${row.id ?? rowIndex}-${i}`, text: formatCellText(base.baseTubeQty, base.baseCrushedQty), note: base.baseNote || "", error: "" }));
    return {
      rowKey: `tpl-${row.id ?? rowIndex}`, isOrphan: false, order: rowIndex + 1,
      plate: savedRow ? (savedRow.plate ?? "") : (row.vehiclePlate || ""), route: savedRow ? (savedRow.route ?? "") : (row.route || ""),
      tplPlate: row.vehiclePlate || "", tplRoute: row.route || "",
      driverName: savedRow ? (savedRow.driverName || "") : "", cells, rowError: "", detailsOpen: false, copyDate: "",
    };
  });
  const orphans = savedPlanRows.filter(saved => saved.orphan).map(saved => {
    const rowKey = `orphan-${++orphanSeq}`;
    return {
      rowKey, isOrphan: true, includeInLineImage: !!saved.include, order: null, plate: saved.plate || "", route: saved.route || "",
      driverName: saved.driverName || "", cells: savedCellsToGrid(saved, `plan-${rowKey}`), rowError: "", detailsOpen: false, copyDate: "",
    };
  });
  return [...templateBased, ...orphans];
};

const rowTotals = row => row.cells.reduce((acc, cell) => {
  const parsed = parseCellText(cell.text);
  return parsed ? { tube: acc.tube + parsed.tubeQty, crushed: acc.crushed + parsed.crushedQty } : acc;
}, { tube: 0, crushed: 0 });

const emptyTemplateRow = () => ({ id: null, route: "", vehiclePlate: "", trips: [{ baseTubeQty: 0, baseCrushedQty: 0, baseNote: "" }] });

// Same local read-from-localStorage pattern ScadaPage.js uses for "scada:edit" — no shared
// permission hook exists in this codebase (see that file's hasScadaEdit). superadmin:* is always
// allowed, same as every other permission gate in this app.
const hasTemplateEditPermission = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const perms = user?.permissions || [];
  return perms.includes("superadmin:*") || perms.includes("report:write");
};

// Deliberately NOT toISOString().slice(0,10) — that round-trips through UTC, which silently
// shifts the calendar date backward by a day for any positive-offset timezone (verified: this
// shifted 2026-09-22 minus 1 day to "2026-09-20" instead of "2026-09-21" under Asia/Bangkok,
// ICECYS's actual deployment timezone, +7). Stays in local date arithmetic throughout instead.
const dateDaysBefore = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// "รายการที่ผ่านมา" — read-only, no edit affordance anywhere here (brief: ด้านล่างแสดงเฉยๆ).
// Fetches each of the last `daysCount` businessDates individually (no bulk range endpoint exists
// yet) and groups client-side by (route, vehiclePlate) — acceptable at this data volume (one
// factory, a handful of routes/day) without adding new backend surface for a read-only strip.
const LoadOutHistorySection = ({ headers, anchorDate }) => {
  const [daysCount, setDaysCount] = useState(7);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const dates = Array.from({ length: daysCount }, (_, i) => dateDaysBefore(anchorDate, i + 1));
    const responses = await Promise.all(dates.map(d => fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out?businessDate=${d}`, { headers: headers() })));
    const dataByDate = await Promise.all(responses.map(r => r.ok ? r.json() : []));
    const built = dates.map((d, i) => {
      const byKey = new Map();
      dataByDate[i].forEach(e => {
        const key = rowKeyOf(e.route, e.vehiclePlate);
        if (!byKey.has(key)) byKey.set(key, { route: e.route || "-", plate: e.vehiclePlate || "-", trips: 0, totalTube: 0, totalCrushed: 0, notes: [] });
        const g = byKey.get(key);
        g.trips += 1; g.totalTube += number(e.tubeQty); g.totalCrushed += number(e.crushedQty);
        if (e.note) g.notes.push(e.note);
      });
      return { date: d, rows: [...byKey.values()] };
    }).filter(group => group.rows.length > 0);
    setGroups(built);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysCount, anchorDate]);

  useEffect(() => { load(); }, [load]);

  return <div style={ledgerCard}>
    <h3 style={sectionTitle}>รายการที่ผ่านมา</h3>
    <p style={helper}>ย้อนหลังจากวันที่เลือกด้านบน — ของจริงจาก IMD แสดงเฉยๆ ไม่มีปุ่มแก้ไขตรงนี้</p>
    {groups.map(group => <div key={group.date} style={{ marginTop: 14 }}>
      <p style={{ ...helper, fontWeight: 700, color: "#334155" }}>{group.date}</p>
      <div style={tableWrap}><table style={ledgerTable}><thead><tr>
        <th style={rowHeader}>สาย</th><th style={rowHeader}>ทะเบียนรถ</th><th style={numberHeader}>จำนวนต๊ง</th><th style={numberHeader}>รวม</th><th style={rowHeader}>หมายเหตุ</th>
      </tr></thead><tbody>
        {group.rows.map((row, i) => <tr key={i}>
          <td style={rowHeader}>{row.route}</td><td style={rowHeader}>{row.plate}</td>
          <td style={sumCell}>{row.trips}</td><td style={sumCell}>{row.totalTube}-{row.totalCrushed}</td>
          <td style={rowHeader}>{row.notes.join("; ") || "-"}</td>
        </tr>)}
      </tbody></table></div>
    </div>)}
    {!loading && !groups.length && <p style={emptyRow}>ไม่พบข้อมูลย้อนหลัง</p>}
    <div style={actions}><button type="button" style={editButton} onClick={() => setDaysCount(current => current + 7)} disabled={loading}>{loading ? "กำลังโหลด..." : "โหลดเพิ่ม"}</button></div>
  </div>;
};

export const LoadOutTab = ({ businessDate, setBusinessDate, routeOptions, headers, pendingEditRecordId, onEditHandled, dailyNote, setDailyNoteField, saveDailyNote }) => {
  const [gridRows, setGridRows] = useState([]);
  const [vehiclePlates, setVehiclePlates] = useState([]);
  const [driverNames, setDriverNames] = useState([]);
  const [realEdit, setRealEdit] = useState(null);
  const [sending, setSending] = useState(false);
  const [topMessage, setTopMessage] = useState("");
  const [sentImageUrl, setSentImageUrl] = useState(null);
  const [template, setTemplate] = useState({ rows: [] });
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState([]);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateMessage, setTemplateMessage] = useState("");
  const nextCellId = useRef(0);
  const planDateRef = useRef(null);

  const loadTemplate = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/template`, { headers: headers() });
    setTemplate(response.ok ? await response.json() : { rows: [] });
    setTemplateLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  // Rebuilds the grid whenever the date OR the template itself changes (e.g. right after
  // "บันทึกแม่แบบ"). Waits for the template's first load so the grid isn't built once against an
  // empty template and then rebuilt a moment later. Reads NO load-out records — see buildGridRows.
  useEffect(() => {
    if (!templateLoaded) return;
    planDateRef.current = null;
    setGridRows(buildGridRows(template.rows, readSavedPlan(businessDate)));
    planDateRef.current = businessDate;
    setSentImageUrl(null);
    setTopMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate, template, templateLoaded]);

  // Remember today's plan edits in this browser only (per businessDate) — never sent to the DB.
  useEffect(() => {
    if (planDateRef.current !== businessDate) return;
    writeSavedPlan(businessDate, gridRows);
  }, [gridRows, businessDate]);

  const resetPlanToTemplate = () => {
    try { localStorage.removeItem(planStorageKey(businessDate)); } catch (_) { /* ignore */ }
    setGridRows(buildGridRows(template.rows, []));
    setSentImageUrl(null);
    setTopMessage("คืนค่าแผนตามแม่แบบแล้ว");
  };

  useEffect(() => {
    const run = async () => {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/vehicle-plates`, { headers: headers() });
      setVehiclePlates(response.ok ? await response.json() : []);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Driver-name suggestions only — read from the day's real records for the datalist, never used
  // to fill the plan form.
  const loadDriverNames = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out?businessDate=${businessDate}`, { headers: headers() });
    const data = response.ok ? await response.json() : [];
    setDriverNames([...new Set(data.map(entry => entry.driverName).filter(Boolean))]);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate]);

  useEffect(() => { loadDriverNames(); }, [loadDriverNames]);

  // สรุป tab's "แก้ไข" on a ต๊ง lands here: opens ONE real bag_load_out_records row in a separate
  // "แก้ไขของจริง" panel. It is unrelated to the plan form / ส่งแผนเข้า LINE — its own save button
  // PUTs only this row's id.
  useEffect(() => {
    if (!pendingEditRecordId || !templateLoaded) return;
    const run = async () => {
      const data = await loadDriverNames();
      const record = data.find(entry => entry.id === pendingEditRecordId);
      if (!record) { setTopMessage("ไม่พบต๊งที่จะแก้ไข (อาจถูกลบไปแล้ว)"); }
      else {
        setRealEdit({
          id: record.id, businessDate: record.businessDate || businessDate, sellerUid: record.sellerUid,
          originalRoute: record.route || "", route: record.route || "", plate: record.vehiclePlate || "",
          driverName: record.driverName || "", text: formatCellText(record.tubeQty, record.crushedQty), note: record.note || "",
          error: "", busy: false,
        });
        setTopMessage("");
      }
      onEditHandled();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditRecordId, templateLoaded]);

  const setRealEditField = (field, value) => setRealEdit(current => current && ({ ...current, [field]: value, error: "" }));
  const cancelRealEdit = () => { setRealEdit(null); setTopMessage(""); };

  const saveRealEdit = async () => {
    const edit = realEdit;
    const parsed = parseCellText(edit.text);
    if (!parsed) { setRealEdit({ ...edit, error: "รูปแบบไม่ถูกต้อง (พิมพ์แบบ 28-16)" }); return; }
    setRealEdit({ ...edit, busy: true, error: "" });
    const body = {
      businessDate: edit.businessDate, route: edit.route.trim(),
      sellerUid: uidForRoute(routeOptions, edit.route) ?? (edit.route === edit.originalRoute ? edit.sellerUid : undefined),
      driverName: edit.driverName || "", vehiclePlate: edit.plate, note: edit.note || "",
      tubeQty: parsed.tubeQty, crushedQty: parsed.crushedQty,
    };
    try {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/${edit.id}`, { method: "PUT", headers: headers(), body: JSON.stringify(body) });
      if (!response.ok) { setRealEdit({ ...edit, busy: false, error: (await response.text()) || "บันทึกไม่สำเร็จ" }); return; }
      setRealEdit(null);
      setTopMessage("บันทึกการแก้ไขต๊งของจริงแล้ว");
      loadDriverNames();
    } catch (e) {
      setRealEdit({ ...edit, busy: false, error: "บันทึกไม่สำเร็จ: " + e.message });
    }
  };

  const deleteRealEdit = async () => {
    const edit = realEdit;
    if (!window.confirm("ลบต๊งนี้? การลบนี้ลบข้อมูลที่บันทึกไว้จริง")) return;
    setRealEdit({ ...edit, busy: true, error: "" });
    try {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/${edit.id}`, { method: "DELETE", headers: headers() });
      if (!response.ok && response.status !== 204) { setRealEdit({ ...edit, busy: false, error: "ลบไม่สำเร็จ" }); return; }
      setRealEdit(null);
      setTopMessage("ลบต๊งของจริงแล้ว");
      loadDriverNames();
    } catch (e) {
      setRealEdit({ ...edit, busy: false, error: "ลบไม่สำเร็จ: " + e.message });
    }
  };

  // --- แก้แม่แบบ (report:write only) -------------------------------------------------
  const startEditTemplate = () => {
    setTemplateDraft((template.rows.length ? template.rows : []).map(row => ({
      id: row.id, route: row.route || "", vehiclePlate: row.vehiclePlate || "",
      trips: (row.trips.length ? row.trips : [{ baseTubeQty: 0, baseCrushedQty: 0, baseNote: "" }]).map(t => ({ ...t, baseNote: t.baseNote || "" })),
    })));
    setTemplateMessage("");
    setEditingTemplate(true);
  };
  const cancelEditTemplate = () => { setEditingTemplate(false); setTemplateDraft([]); setTemplateMessage(""); };
  const addTemplateRow = () => setTemplateDraft(current => [...current, emptyTemplateRow()]);
  const removeTemplateRow = index => setTemplateDraft(current => current.filter((_, i) => i !== index));
  const moveTemplateRow = (index, direction) => setTemplateDraft(current => {
    const target = index + direction;
    if (target < 0 || target >= current.length) return current;
    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const setTemplateRowField = (index, key, value) => setTemplateDraft(current => current.map((row, i) => i === index ? { ...row, [key]: value } : row));
  const addTemplateTrip = index => setTemplateDraft(current => current.map((row, i) => i === index ? { ...row, trips: [...row.trips, { baseTubeQty: 0, baseCrushedQty: 0, baseNote: "" }] } : row));
  const removeTemplateTrip = (rowIndex, tripIndex) => setTemplateDraft(current => current.map((row, i) => i === rowIndex ? { ...row, trips: row.trips.filter((_, ti) => ti !== tripIndex) } : row));
  const setTemplateTripField = (rowIndex, tripIndex, key, value) => setTemplateDraft(current => current.map((row, i) => i !== rowIndex ? row
    : { ...row, trips: row.trips.map((t, ti) => ti === tripIndex ? { ...t, [key]: value } : t) }));

  // "สร้างจากที่บันทึกล่าสุด" — replaces the CURRENT draft with a preview built server-side from
  // the most recent businessDate that has any load-out data. Nothing is persisted until the
  // accountant reviews it here and clicks "บันทึกแม่แบบ" below.
  const buildFromLatestTemplate = async () => {
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/template/build-from-latest`, { method: "POST", headers: headers() });
    if (!response.ok) { setTemplateMessage((await response.text()) || "ดึงข้อมูลจากที่บันทึกล่าสุดไม่สำเร็จ"); return; }
    const data = await response.json();
    if (!data.rows.length) { setTemplateMessage("ยังไม่มีข้อมูลขึ้นน้ำแข็งที่เคยบันทึกไว้เลย"); return; }
    setTemplateDraft(data.rows.map(row => ({
      id: null, route: row.route || "", vehiclePlate: row.vehiclePlate || "",
      trips: (row.trips.length ? row.trips : [{ baseTubeQty: 0, baseCrushedQty: 0, baseNote: "" }]).map(t => ({ ...t, baseNote: t.baseNote || "" })),
    })));
    setTemplateMessage('ดึงมาจากวันล่าสุดที่มีข้อมูลแล้ว — ตรวจสอบก่อนกด "บันทึกแม่แบบ"');
  };

  const saveTemplateDraft = async () => {
    setTemplateSaving(true);
    setTemplateMessage("");
    const body = {
      rows: templateDraft.map(row => ({
        route: row.route || "", vehiclePlate: row.vehiclePlate || "",
        trips: row.trips.map(t => ({ baseTubeQty: number(t.baseTubeQty), baseCrushedQty: number(t.baseCrushedQty), baseNote: (t.baseNote || "").trim() || null })),
      })),
    };
    try {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/template`, { method: "PUT", headers: headers(), body: JSON.stringify(body) });
      if (!response.ok) { setTemplateMessage((await response.text()) || "บันทึกแม่แบบไม่สำเร็จ"); return; }
      setTemplate(await response.json());
      setEditingTemplate(false);
      setTemplateDraft([]);
      setTemplateMessage("บันทึกแม่แบบแล้ว");
    } catch (e) {
      setTemplateMessage("บันทึกแม่แบบไม่สำเร็จ: " + e.message);
    } finally {
      setTemplateSaving(false);
    }
  };

  const setCellError = (rowKey, cellId, message) => setGridRows(current => current.map(row => row.rowKey !== rowKey ? row
    : { ...row, cells: row.cells.map(cell => cell.cellId === cellId ? { ...cell, error: message } : cell) }));
  const setRowError = (rowKey, message) => setGridRows(current => current.map(row => row.rowKey === rowKey ? { ...row, rowError: message } : row));
  const setRowDriverName = (rowKey, value) => setGridRows(current => current.map(row => row.rowKey === rowKey ? { ...row, driverName: value } : row));
  const setRowField = (rowKey, field, value) => setGridRows(current => current.map(row => row.rowKey === rowKey ? { ...row, [field]: value } : row));
  const setRowCopyDate = (rowKey, value) => setGridRows(current => current.map(row => row.rowKey === rowKey ? { ...row, copyDate: value } : row));
  const addPlanRow = () => {
    nextCellId.current += 1;
    orphanSeq += 1;
    const rowKey = `orphan-${orphanSeq}`;
    setGridRows(current => [...current, {
      rowKey, isOrphan: true, includeInLineImage: false, order: null, plate: "", route: "", driverName: "",
      cells: [{ cellId: nextCellId.current, text: "", note: "", error: "" }], rowError: "", detailsOpen: false, copyDate: "",
    }]);
  };
  const removePlanRow = rowKey => setGridRows(current => current.filter(row => row.rowKey !== rowKey));
  const addTripToRow = rowKey => {
    nextCellId.current += 1;
    const cellId = nextCellId.current;
    setGridRows(current => current.map(row => row.rowKey === rowKey ? { ...row, cells: [...row.cells, { cellId, text: "", note: "", error: "" }] } : row));
  };
  const toggleDetails = rowKey => setGridRows(current => current.map(row => row.rowKey === rowKey ? { ...row, detailsOpen: !row.detailsOpen } : row));

  // Typing into an existing cell (colIndex < cells.length) updates it in place. Typing into the
  // one slot PAST the last real cell (colIndex === cells.length) promotes it into a new real
  // cell — that's the "always one blank T slot ready to fill" behavior; columns beyond that stay
  // disabled "-" placeholders (rendered, not stored) until the row grows up to them.
  const onCellTextChange = (rowKey, colIndex, value) => setGridRows(current => current.map(row => {
    if (row.rowKey !== rowKey) return row;
    if (colIndex < row.cells.length) return { ...row, cells: row.cells.map((cell, i) => i === colIndex ? { ...cell, text: value, error: "" } : cell) };
    if (colIndex === row.cells.length) {
      nextCellId.current += 1;
      return { ...row, cells: [...row.cells, { cellId: nextCellId.current, text: value, note: "", error: "" }] };
    }
    return row;
  }));

  const onCellNoteChange = (rowKey, colIndex, value) => setGridRows(current => current.map(row => {
    if (row.rowKey !== rowKey || colIndex >= row.cells.length) return row;
    return { ...row, cells: row.cells.map((cell, i) => i === colIndex ? { ...cell, note: value } : cell) };
  }));

  // Format validation happens on blur (not every keystroke) — matches saveAllRows' own check so
  // an unresolved format error is visible before the user ever clicks save.
  const onCellBlur = (rowKey, colIndex) => setGridRows(current => current.map(row => {
    if (row.rowKey !== rowKey || colIndex >= row.cells.length) return row;
    const cell = row.cells[colIndex];
    const parsed = parseCellText(cell.text);
    const message = parsed === null ? "รูปแบบไม่ถูกต้อง (พิมพ์แบบ 28-16)" : "";
    return { ...row, cells: row.cells.map((c, i) => i === colIndex ? { ...c, error: message } : c) };
  }));

  // Per-row "คัดลอกจากวันที่..." — explicit, read-only pull of another day's real trips for this
  // row's route (and its driver name) into the plan row (nothing is written back to those records).
  const copyRowFromDate = async rowKey => {
    const row = gridRows.find(candidate => candidate.rowKey === rowKey);
    if (!row?.copyDate || !row.route) { setRowError(rowKey, "เลือกวันที่และสายก่อนดึงข้อมูล"); return; }
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-load-out/by-route?businessDate=${row.copyDate}&route=${encodeURIComponent(row.route)}`, { headers: headers() });
    if (!response.ok) { setRowError(rowKey, "ดึงข้อมูลจากวันอื่นไม่สำเร็จ"); return; }
    const list = await response.json();
    if (!list.length) { setRowError(rowKey, `ไม่พบข้อมูลขึ้นน้ำแข็งของสายนี้ในวันที่ ${row.copyDate}`); return; }
    const cells = list.map(item => {
      nextCellId.current += 1;
      return { cellId: nextCellId.current, text: formatCellText(item.tubeQty, item.crushedQty), note: item.note || "", error: "" };
    });
    setGridRows(current => current.map(r => r.rowKey === rowKey ? { ...r, driverName: list[0].driverName || r.driverName, cells, rowError: "" } : r));
  };

  // Sends the plan image built from the form's current values — no bag-load-out request of any
  // kind (POST/PUT/DELETE) is made from here. A malformed cell blocks the send (the image would
  // otherwise silently drop it). Machine times are saved first as before (bag_ledger_daily_note is
  // not IMD's table) and also travel in the payload so the footer can't lag behind the inputs.
  const sendPlan = async () => {
    setTopMessage("");
    const badRows = [];
    const planRows = gridRows.filter(row => !row.isOrphan || row.includeInLineImage).map(row => {
      const trips = [];
      let rowFailed = false;
      row.cells.forEach(cell => {
        if (!cell.text.trim()) return;
        const parsed = parseCellText(cell.text);
        if (!parsed) { setCellError(row.rowKey, cell.cellId, "รูปแบบไม่ถูกต้อง (พิมพ์แบบ 28-16)"); rowFailed = true; return; }
        trips.push({ tubeQty: parsed.tubeQty, crushedQty: parsed.crushedQty, note: (cell.note || "").trim() || null });
      });
      if (rowFailed) badRows.push(row.plate || row.route || "(ไม่ทราบสาย)");
      return { plate: row.plate || null, route: row.route || null, driverName: (row.driverName || "").trim() || null, trips };
    });
    if (badRows.length) { setTopMessage(`รูปแบบตัวเลขไม่ถูกต้อง (${badRows.join(", ")}) — แก้ให้ครบก่อนถึงจะส่ง LINE ได้`); return; }
    setSending(true);
    try {
      const noteSave = await saveDailyNote();
      if (!noteSave.ok) { setTopMessage(noteSave.message); return; }
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger/vehicle-plan/send`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({
          businessDate,
          rows: planRows,
          dailyNote: {
            businessDate,
            machine1PowerOnTime: dailyNote.machine1PowerOnTime || null,
            machine1PowerOffTime: dailyNote.machine1PowerOffTime || null,
            machine2PowerOnTime: dailyNote.machine2PowerOnTime || null,
            machine2PowerOffTime: dailyNote.machine2PowerOffTime || null,
            machine1SpecialNote: dailyNote.machine1SpecialNote || null,
            machine2SpecialNote: dailyNote.machine2SpecialNote || null,
            machine1StartsWith: dailyNote.machine1StartsWith || null,
            machine2StartsWith: dailyNote.machine2StartsWith || null,
          },
        }),
      });
      if (!response.ok) { setTopMessage((await response.text()) || "ส่งไม่สำเร็จ"); return; }
      const data = await response.json();
      setSentImageUrl(data.imageUrl);
      setTopMessage("ส่งแผนเข้า LINE แล้ว");
    } catch (e) {
      setTopMessage("ส่งไม่สำเร็จ: " + e.message);
    } finally {
      setSending(false);
    }
  };

  const maxCols = Math.max(1, ...gridRows.map(row => row.cells.length));
  const totalCols = maxCols * 2 + 5;
  const canEditTemplate = hasTemplateEditPermission();
  const orphanCount = gridRows.filter(row => row.isOrphan).length;

  // ปิด→เปิด แปลว่าวันนั้นเครื่องเริ่มด้วยเหตุการณ์ปิด (เช่นหยุดพักถ่ายน้ำทิ้งกลางดึกแล้วเปิดใหม่) —
  // สลับแค่ป้าย/ลำดับที่โชว์ ไม่ได้เปลี่ยนความหมายของ machineNPowerOnTime/OffTime เอง (Vector
  // 2026-09-29, relayed via exa). null = "เปิด→ปิด" (พฤติกรรมเดิม).
  const setMachineStartsWith = (machineNum, value) => setDailyNoteField(`machine${machineNum}StartsWith`, value);

  const machineTimeInputs = machineNum => {
    const onKey = `machine${machineNum}PowerOnTime`, offKey = `machine${machineNum}PowerOffTime`;
    const isOffFirst = dailyNote[`machine${machineNum}StartsWith`] === "OFF";
    const onInput = <label key="on" style={smallLabel}>เปิด<input type="time" value={dailyNote[onKey]} onChange={event => setDailyNoteField(onKey, event.target.value)} onBlur={saveDailyNote} style={control} /></label>;
    const offInput = <label key="off" style={smallLabel}>ปิด<input type="time" value={dailyNote[offKey]} onChange={event => setDailyNoteField(offKey, event.target.value)} onBlur={saveDailyNote} style={control} /></label>;
    return isOffFirst ? [offInput, onInput] : [onInput, offInput];
  };

  const machineOrderToggleButtons = machineNum => {
    const startsWith = dailyNote[`machine${machineNum}StartsWith`];
    const isOffFirst = startsWith === "OFF";
    const choose = value => { setMachineStartsWith(machineNum, value); saveDailyNote({ [`machine${machineNum}StartsWith`]: value }); };
    return <div style={machineOrderToggleRow}>
      <button type="button" style={isOffFirst ? machineOrderToggle : machineOrderToggleActive} onClick={() => choose(null)}>เปิด→ปิด</button>
      <button type="button" style={isOffFirst ? machineOrderToggleActive : machineOrderToggle} onClick={() => choose("OFF")}>ปิด→เปิด</button>
    </div>;
  };

  return <>
    {editingTemplate && <div style={ledgerCard}>
      <div style={formHeader}>
        <div><h3 style={sectionTitle}>แก้แม่แบบขึ้นน้ำแข็ง</h3><p style={helper}>แถว/สาย/ทะเบียน/จำนวนต๊ง/ค่า base ตรงนี้ใช้ร่วมกันทั้งโรงงาน ทุกเครื่องเห็นค่าเดียวกัน</p></div>
      </div>
      {!templateDraft.length && <div style={warnBanner}>ยังไม่มีแถวในแม่แบบ — กด "+ เพิ่มแถว" หรือ "สร้างจากที่บันทึกล่าสุด" ด้านล่าง</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
        {templateDraft.map((row, rowIndex) => <div key={rowIndex} style={templateRowCard}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
            <label style={tRowField}>ทะเบียนรถ (ถ้ามี)<input value={row.vehiclePlate} onChange={event => setTemplateRowField(rowIndex, "vehiclePlate", event.target.value)} list="vehicle-plate-options-loadout" style={control} placeholder="เช่น 1087" /></label>
            {/* Free-typed, not a <select> restricted to routeOptions — a template row can be a
                stop that isn't a seller account at all ("หน้าโรง", "ชนาธิป"), so the form must
                accept any text, with known routes offered as datalist suggestions only. */}
            <label style={tRowField}>สาย<input value={row.route} onChange={event => setTemplateRowField(rowIndex, "route", event.target.value)} list="route-options-template" style={control} placeholder="เช่น ตะแบง หรือ หน้าโรง" /></label>
            <button type="button" style={editButton} onClick={() => moveTemplateRow(rowIndex, -1)} disabled={rowIndex === 0} title="ย้ายขึ้น">↑</button>
            <button type="button" style={editButton} onClick={() => moveTemplateRow(rowIndex, 1)} disabled={rowIndex === templateDraft.length - 1} title="ย้ายลง">↓</button>
            <button type="button" style={rowDeleteButton} onClick={() => removeTemplateRow(rowIndex)}>ลบแถวนี้</button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
            {row.trips.map((trip, tripIndex) => <div key={tripIndex} style={tRow}>
              <span style={tRowLabel}>T.{tripIndex + 1}</span>
              <label style={tRowField}>หลอด (base)<input type="number" min="0" value={trip.baseTubeQty} onChange={event => setTemplateTripField(rowIndex, tripIndex, "baseTubeQty", event.target.value)} style={control} /></label>
              <label style={tRowField}>บด (base)<input type="number" min="0" value={trip.baseCrushedQty} onChange={event => setTemplateTripField(rowIndex, tripIndex, "baseCrushedQty", event.target.value)} style={control} /></label>
              <label style={tRowField}>note T.{tripIndex + 1}<input type="text" value={trip.baseNote || ""} maxLength={255} onChange={event => setTemplateTripField(rowIndex, tripIndex, "baseNote", event.target.value)} style={{ ...control, ...noteInput }} placeholder="หมายเหตุ" /></label>
              {row.trips.length > 1 && <button type="button" style={rowRemoveButton} onClick={() => removeTemplateTrip(rowIndex, tripIndex)} title="ลบต๊งนี้">×</button>}
            </div>)}
          </div>
          <div style={{ marginTop: 8 }}><button type="button" style={editButton} onClick={() => addTemplateTrip(rowIndex)}>+ เพิ่มต๊ง (T)</button></div>
        </div>)}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="button" style={editButton} onClick={addTemplateRow}>+ เพิ่มแถว</button>
        <button type="button" style={editButton} onClick={buildFromLatestTemplate}>สร้างจากที่บันทึกล่าสุด</button>
      </div>
      {templateMessage && <p style={messageStyle(templateMessage)}>{templateMessage}</p>}
      <div style={actions}>
        <button type="button" style={saveButton} onClick={saveTemplateDraft} disabled={templateSaving}>{templateSaving ? "กำลังบันทึก..." : "บันทึกแม่แบบ"}</button>
        <button type="button" style={backButton} onClick={cancelEditTemplate}>ยกเลิก</button>
      </div>
    </div>}

    {realEdit && <div style={realEditCard} data-testid="real-edit-panel">
      <div style={formHeader}>
        <div>
          <h3 style={{ ...sectionTitle, color: "#9a3412" }}>แก้ไขของจริง (ต๊งที่บันทึกไว้จริง)</h3>
          <p style={helper}>โหมดนี้แก้ต๊งเดียวในตารางขึ้นกระสอบจริง (ที่ IMD ส่งมา) — <b>ไม่เกี่ยวกับแผน/ปุ่มส่งแผนเข้า LINE</b> · กด "บันทึกการแก้ไข" เพื่อเขียนทับต๊งนี้ หรือ "ยกเลิก" เพื่อกลับไปโหมดแผน</p>
        </div>
      </div>
      <div style={identityGrid}>
        <label style={label}>สาย<input list="route-options-template" value={realEdit.route} onChange={event => setRealEditField("route", event.target.value)} style={control} /></label>
        <label style={label}>ทะเบียนรถ<input list="vehicle-plate-options-loadout" value={realEdit.plate} onChange={event => setRealEditField("plate", event.target.value)} style={control} /></label>
        <label style={label}>คนขับ (ถ้ามี)<input list="bag-load-out-driver-history" value={realEdit.driverName} onChange={event => setRealEditField("driverName", event.target.value)} style={control} placeholder="เลือกชื่อเดิม หรือพิมพ์ชื่อ" /></label>
        <label style={label}>หลอด-บด (พิมพ์แบบ 28-16)<input value={realEdit.text} onChange={event => setRealEditField("text", event.target.value)} style={control} placeholder="28-16" /></label>
        <label style={label}>หมายเหตุ<input value={realEdit.note} onChange={event => setRealEditField("note", event.target.value)} style={control} placeholder="หมายเหตุ" /></label>
      </div>
      {realEdit.error && <p style={error}>{realEdit.error}</p>}
      <div style={actions}>
        <button type="button" style={saveButton} onClick={saveRealEdit} disabled={realEdit.busy}>บันทึกการแก้ไข</button>
        <button type="button" style={backButton} onClick={cancelRealEdit} disabled={realEdit.busy}>ยกเลิก</button>
        <button type="button" style={dangerButton} onClick={deleteRealEdit} disabled={realEdit.busy}>ลบต๊งนี้</button>
      </div>
    </div>}

    <div style={realEdit ? { ...ledgerCard, display: "none" } : ledgerCard}>
      <div style={formHeader}>
        <div><h3 style={sectionTitle}>ขึ้นน้ำแข็ง</h3><p style={helper}>นี่คือ "แผน" — แก้เฉพาะช่องที่เปลี่ยน แล้วกด "ส่งแผนเข้า LINE" ด้านล่าง ระบบส่งรูปตามค่าที่เห็นในฟอร์มนี้ <b>โดยไม่บันทึกลงตารางขึ้นกระสอบจริง</b> (ของจริงมาจาก IMD) — แถว/ทะเบียนรถ/สาย/จำนวนต๊งมาจากแม่แบบที่ตั้งไว้ ช่องตัวเลขเปิดมาเป็นค่า base · ค่าที่แก้ไว้จำในเครื่องนี้ต่อวัน · แก้ทะเบียน/สายของแผนวันนี้ได้ที่ "แก้รายละเอียดสายนี้" (ไม่เปลี่ยนแม่แบบ) · "+ เพิ่มแถว" เพิ่มแถวนอกแม่แบบเฉพาะแผนวันนี้</p></div>
        <div style={{ display: "flex", gap: 8 }}><button type="button" style={editButton} onClick={resetPlanToTemplate}>คืนค่าตามแม่แบบ</button>{canEditTemplate && !editingTemplate && <button type="button" style={editButton} onClick={startEditTemplate}>แก้แม่แบบ</button>}</div>
      </div>

      <label style={{ ...label, maxWidth: 220, marginBottom: 16 }}>วันที่<input type="date" value={businessDate} onChange={event => setBusinessDate(event.target.value)} style={control} /></label>

      {templateLoaded && !template.rows.length && !editingTemplate && <div style={warnBanner}>ยังไม่มีแม่แบบขึ้นน้ำแข็ง — {canEditTemplate ? 'กด "แก้แม่แบบ" ด้านบนเพื่อสร้าง' : "รอให้บัญชีตั้งแม่แบบก่อนถึงจะกรอกได้"}</div>}

      {orphanCount > 0 && <div style={warnBanner}>มี {orphanCount} แถวนอกแม่แบบ (เพิ่มเองสำหรับแผนวันนี้) — จะไม่อยู่ในรูปที่ส่ง LINE เว้นแต่ติ๊ก "ใส่ในรูป LINE" · ไม่ได้บันทึกลงแม่แบบหรือตารางของจริง</div>}

      <style>{scrollbarCss}</style>
      <div style={tableWrap} className="bagLedgerScrollX"><table style={ledgerTable}><thead>
        <tr>
          <th style={stickyHeaderCell(0)}>ลำดับ</th><th style={stickyHeaderCell(1)}>ทะเบียนรถ</th><th style={stickyHeaderCell(2)}>สาย</th>
          {Array.from({ length: maxCols }, (_, i) => <th key={`th-${i}`} style={numberHeaderNarrow}>T.{i + 1}</th>)}
          <th style={numberHeaderNarrow}>รวม</th>
          {Array.from({ length: maxCols }, (_, i) => <th key={`nh-${i}`} style={numberHeader}>note T.{i + 1}</th>)}
          <th style={rowHeader} />
        </tr>
      </thead><tbody>
        {gridRows.map(row => {
          const rowBg = "#fff";
          const totals = rowTotals(row);
          return <React.Fragment key={row.rowKey}>
          <tr id={`bag-ledger-row-${row.rowKey}`}>
            <td style={stickyBodyCell(0, rowBg)}>{row.isOrphan ? "-" : row.order}</td>
            <td style={stickyBodyCell(1, rowBg)}>{row.isOrphan
              ? <>
                  <input value={row.plate} onChange={event => setRowField(row.rowKey, "plate", event.target.value)} list="vehicle-plate-options-loadout" style={{ ...control, ...identityInput }} placeholder="ทะเบียนรถ" />
                  <label style={includeLabel}><input type="checkbox" checked={row.includeInLineImage} onChange={event => setRowField(row.rowKey, "includeInLineImage", event.target.checked)} />ใส่ในรูป LINE</label>
                </>
              : <>{row.plate || "-"}{row.plate !== row.tplPlate && <div style={editedHint}>แม่แบบ: {row.tplPlate || "-"}</div>}</>}</td>
            <td style={stickyBodyCell(2, rowBg)}>{row.isOrphan
              ? <>
                  <input value={row.route} onChange={event => setRowField(row.rowKey, "route", event.target.value)} list="route-options-template" style={{ ...control, ...identityInput }} placeholder="สาย" />
                  <button type="button" style={{ ...rowDeleteButton, marginTop: 4 }} onClick={() => removePlanRow(row.rowKey)}>ลบแถวนี้</button>
                </>
              : <>{row.route || "-"}{row.route !== row.tplRoute && <div style={editedHint}>แม่แบบ: {row.tplRoute || "-"}</div>}</>}</td>
            {Array.from({ length: maxCols }, (_, col) => <td key={col} style={sumCell}>
              {col < row.cells.length
                ? <div>
                    <input value={row.cells[col].text} onChange={event => onCellTextChange(row.rowKey, col, event.target.value)} onBlur={() => onCellBlur(row.rowKey, col)} style={{ ...control, ...cellInput }} placeholder="28-16" />
                    {row.cells[col].error && <div style={cellErrorText}>{row.cells[col].error}</div>}
                  </div>
                : "-"}
            </td>)}
            <td style={sumCell}>{row.cells.length ? `${totals.tube}-${totals.crushed}` : "-"}</td>
            {Array.from({ length: maxCols }, (_, col) => <td key={`n${col}`} style={sumCell}>
              {col < row.cells.length
                ? <input value={row.cells[col].note} onChange={event => onCellNoteChange(row.rowKey, col, event.target.value)} style={{ ...control, ...noteInput }} placeholder="หมายเหตุ" />
                : "-"}
            </td>)}
            <td style={rowHeader}><button type="button" style={detailsToggle} onClick={() => toggleDetails(row.rowKey)}>{row.detailsOpen ? "ซ่อนรายละเอียด" : "แก้รายละเอียดสายนี้"}</button></td>
          </tr>
          {row.rowError && <tr><td colSpan={totalCols} style={{ ...error, padding: "4px 12px" }}>{row.rowError}</td></tr>}
          {row.detailsOpen && <tr><td colSpan={totalCols} style={detailsCell}>
            <div style={identityGrid}>
              {!row.isOrphan && <>
                <label style={label}>ทะเบียนรถ (แผนวันนี้)<input list="vehicle-plate-options-loadout" value={row.plate} onChange={event => setRowField(row.rowKey, "plate", event.target.value)} style={control} placeholder="แก้ทะเบียนรถ" /></label>
                <label style={label}>สาย (แผนวันนี้)<input list="route-options-template" value={row.route} onChange={event => setRowField(row.rowKey, "route", event.target.value)} style={control} placeholder="แก้สาย" /></label>
              </>}
              <label style={label}>คนขับ (ถ้ามี)<input list="bag-load-out-driver-history" value={row.driverName} onChange={event => setRowDriverName(row.rowKey, event.target.value)} style={control} placeholder="เลือกชื่อเดิม หรือพิมพ์ชื่อ" /></label>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap" }}>
              <button type="button" style={editButton} onClick={() => addTripToRow(row.rowKey)}>+ เพิ่มต๊ง (T)</button>
              <label style={smallLabel}>ไม่ใช่ยอดนี้? คัดลอกจากวันที่...<input type="date" value={row.copyDate} onChange={event => setRowCopyDate(row.rowKey, event.target.value)} style={control} /></label>
              <button type="button" style={editButton} onClick={() => copyRowFromDate(row.rowKey)}>ดึงมา</button>
            </div>
          </td></tr>}
        </React.Fragment>;
        })}
        {!gridRows.length && <tr><td colSpan={totalCols} style={emptyRow}>ยังไม่มีข้อมูล</td></tr>}
      </tbody></table></div>

      <div style={{ marginTop: 10 }}><button type="button" style={editButton} onClick={addPlanRow} disabled={!templateLoaded}>+ เพิ่มแถว</button></div>

      <datalist id="bag-load-out-driver-history">{driverNames.map(name => <option key={name} value={name} />)}</datalist>
      <datalist id="vehicle-plate-options-loadout">{vehiclePlates.map(plate => <option key={plate} value={plate} />)}</datalist>
      <datalist id="route-options-template">{routeOptions.map(option => <option key={option.uid} value={routeValue(option)} />)}</datalist>

      {topMessage && <p style={messageStyle(topMessage)}>{topMessage}</p>}

      <div style={machineTimeRow}>
        <div style={machineTimeGroup}>
          <span style={machineTimeLabel}>เวลาเปิด-ปิดเครื่อง 1</span>
          {machineOrderToggleButtons(1)}
          <div style={machineTimeGroupRow}>{machineTimeInputs(1)}</div>
          <label style={smallLabel}>note พิเศษ (ถ้ามี)<input type="text" placeholder="ถ่ายน้ำทิ้ง" value={dailyNote.machine1SpecialNote || ""} onChange={event => setDailyNoteField("machine1SpecialNote", event.target.value)} onBlur={saveDailyNote} style={control} /></label>
        </div>
        <div style={machineTimeGroup}>
          <span style={machineTimeLabel}>เครื่อง 2</span>
          {machineOrderToggleButtons(2)}
          <div style={machineTimeGroupRow}>{machineTimeInputs(2)}</div>
          <label style={smallLabel}>note พิเศษ (ถ้ามี)<input type="text" placeholder="ถ่ายน้ำทิ้ง" value={dailyNote.machine2SpecialNote || ""} onChange={event => setDailyNoteField("machine2SpecialNote", event.target.value)} onBlur={saveDailyNote} style={control} /></label>
        </div>
      </div>

      <div style={actions}><button type="button" style={saveButton} onClick={sendPlan} disabled={sending}>{sending ? "กำลังส่ง..." : "ส่งแผนเข้า LINE"}</button></div>
      {sentImageUrl && <div style={{ marginTop: 14 }}>
        <p style={helper}>รูปแผนที่ส่ง:</p>
        <img src={sentImageUrl} alt="แผนขึ้นน้ำแข็งที่ส่ง" style={{ maxWidth: "100%", border: "1px solid #d9e1ea", borderRadius: 6 }} />
      </div>}
    </div>

    <LoadOutHistorySection headers={headers} anchorDate={businessDate} />
  </>;
};

const blankRefillRow = () => ({ rowId: null, tubeQty: "", crushedQty: "", collectedTubeQty: "", collectedCrushedQty: "", hasCollected: false, recordId: null, error: "" });
const emptyRefillIdentity = () => ({ vehiclePlate: "", note: "" });

// 2026-08-31 (Vector) — "เพิ่มต๊ง (T)" here too, same block-vs-rows split LoadOutTab already
// uses: vehiclePlate/note are shared per add-session (`identity`), quantities are per-ต๊ง
// (`rows`). Route selection also moved into this tab's own card (was a page-level filter).
const RefillTab = ({ businessDate, setBusinessDate, route, setRoute, routeOptions, headers }) => {
  const [entries, setEntries] = useState([]);
  const [identity, setIdentity] = useState(emptyRefillIdentity());
  const [rows, setRows] = useState([{ ...blankRefillRow(), rowId: 0 }]);
  const [vehiclePlates, setVehiclePlates] = useState([]);
  const [copyDate, setCopyDate] = useState("");
  const [message, setMessage] = useState("");
  const [draftFrom, setDraftFrom] = useState(null);
  const [saving, setSaving] = useState(false);
  const nextRowId = useRef(0);

  const resetForm = () => {
    setIdentity(emptyRefillIdentity());
    nextRowId.current += 1;
    setRows([{ ...blankRefillRow(), rowId: nextRowId.current }]);
    setDraftFrom(null);
  };

  const reload = useCallback(async () => {
    if (!route) { setEntries([]); return; }
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-refill?businessDate=${businessDate}&route=${encodeURIComponent(route)}`, { headers: headers() });
    setEntries(response.ok ? await response.json() : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate, route]);

  useEffect(() => {
    reload();
    resetForm();
    setMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  // "ยกยอดจากวันก่อนหน้า" — same pattern as LoadOutTab; the backend draft endpoint still
  // returns one most-recent record (not an array like LoadOut's draft), so this pre-fills
  // identity + exactly one row. "+ เพิ่มต๊ง (T)" below is for adding more manually.
  useEffect(() => {
    if (!route) return;
    let cancelled = false;
    const run = async () => {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger/bag-refill/draft?businessDate=${businessDate}&route=${encodeURIComponent(route)}`, { headers: headers() });
      if (cancelled || response.status === 404 || !response.ok) return;
      const data = await response.json();
      setIdentity({ vehiclePlate: data.vehiclePlate || "", note: data.note || "" });
      nextRowId.current += 1;
      setRows([{ ...blankRefillRow(), rowId: nextRowId.current, tubeQty: data.tubeQty ?? "", crushedQty: data.crushedQty ?? "",
        collectedTubeQty: data.collectedTubeQty ?? "", collectedCrushedQty: data.collectedCrushedQty ?? "",
        hasCollected: number(data.collectedTubeQty) > 0 || number(data.collectedCrushedQty) > 0 }]);
      setDraftFrom(data.sourceBusinessDate);
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate, route]);

  useEffect(() => {
    const run = async () => {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/vehicle-plates`, { headers: headers() });
      setVehiclePlates(response.ok ? await response.json() : []);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setIdentityField = (key, value) => setIdentity(current => ({ ...current, [key]: value }));
  const updateRow = (rowId, key, value) => setRows(current => current.map(row => row.rowId === rowId ? { ...row, [key]: value } : row));

  // "+ เพิ่มต๊ง (T)" — appends a blank row so several ต๊ง of refill can be entered in one
  // session before saving (2026-08-31, Vector — same pattern LoadOutTab already has).
  const addRow = () => {
    nextRowId.current += 1;
    setRows(current => [...current, { ...blankRefillRow(), rowId: nextRowId.current }]);
  };

  // × discards this row locally only — never deletes a saved record (that's "ลบแถวนี้"
  // below). Never lets the list go fully empty.
  const discardRow = rowId => setRows(current => current.length > 1 ? current.filter(row => row.rowId !== rowId) : current);

  // "ลบแถวนี้" on a row that has a recordId — a real DELETE, unlike × which only discards
  // the in-progress edit.
  const deleteFormRow = async row => {
    if (!window.confirm("ลบรายการเพิ่มน้ำแข็งนี้?")) return;
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-refill/${row.recordId}`, { method: "DELETE", headers: headers() });
    if (!response.ok && response.status !== 204) { updateRow(row.rowId, "error", "ลบไม่สำเร็จ"); return; }
    setRows(current => {
      const remaining = current.filter(candidate => candidate.rowId !== row.rowId);
      return remaining.length ? remaining : [{ ...blankRefillRow(), rowId: row.rowId }];
    });
    await reload();
  };

  // Opens ONE saved entry for editing — replaces whatever's currently in the form (save any
  // in-progress new rows first if you don't want to lose them).
  const editEntry = entry => {
    setIdentity({ vehiclePlate: entry.vehiclePlate || "", note: entry.note || "" });
    nextRowId.current += 1;
    setRows([{ rowId: nextRowId.current, tubeQty: entry.tubeQty ?? "", crushedQty: entry.crushedQty ?? "",
      collectedTubeQty: entry.collectedTubeQty ?? "", collectedCrushedQty: entry.collectedCrushedQty ?? "",
      hasCollected: number(entry.collectedTubeQty) > 0 || number(entry.collectedCrushedQty) > 0,
      recordId: entry.id, error: "" }]);
    setDraftFrom(null);
    setMessage("กำลังแก้ไขรายการเพิ่มน้ำแข็ง");
  };

  const cancelEdit = () => { resetForm(); setMessage(""); };

  const copyFromDate = async () => {
    if (!copyDate || !route) { setMessage("เลือกวันที่และสายก่อนดึงข้อมูล"); return; }
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-refill?businessDate=${copyDate}&route=${encodeURIComponent(route)}`, { headers: headers() });
    if (!response.ok) { setMessage("ดึงข้อมูลจากวันอื่นไม่สำเร็จ"); return; }
    const list = await response.json();
    if (!list.length) { setMessage(`ไม่พบข้อมูลเพิ่มน้ำแข็งของสายนี้ในวันที่ ${copyDate}`); return; }
    const source = list[0];
    setIdentity({ vehiclePlate: source.vehiclePlate || "", note: source.note || "" });
    nextRowId.current += 1;
    setRows([{ ...blankRefillRow(), rowId: nextRowId.current, tubeQty: source.tubeQty ?? "", crushedQty: source.crushedQty ?? "",
      collectedTubeQty: source.collectedTubeQty ?? "", collectedCrushedQty: source.collectedCrushedQty ?? "",
      hasCollected: number(source.collectedTubeQty) > 0 || number(source.collectedCrushedQty) > 0 }]);
    setDraftFrom(null);
    setMessage("ดึงข้อมูลมาแล้ว — ตรวจสอบก่อนกดบันทึก");
  };

  const removeTableEntry = async entry => {
    if (!window.confirm("ลบรายการเพิ่มน้ำแข็งนี้?")) return;
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-refill/${entry.id}`, { method: "DELETE", headers: headers() });
    if (!response.ok && response.status !== 204) { setMessage("ลบไม่สำเร็จ"); return; }
    setMessage("ลบรายการแล้ว");
    reload();
  };

  // Sequential, mirrors LoadOutTab's saveAll — a row that fails stays visible with its own
  // inline error; a row that succeeds is removed immediately. identity (vehiclePlate/note) is
  // shared across every row in this save, same identity-vs-per-ต๊ง-quantity split LoadOutTab
  // already uses.
  const saveAll = async () => {
    if (!route) { setMessage("กรุณาเลือกสาย"); return; }
    setSaving(true);
    setMessage("");
    let anySucceeded = false;
    for (const row of rows) {
      const body = {
        businessDate, route: route.trim(), sellerUid: uidForRoute(routeOptions, route), vehiclePlate: identity.vehiclePlate || "",
        tubeQty: number(row.tubeQty), crushedQty: number(row.crushedQty),
        collectedTubeQty: row.hasCollected ? number(row.collectedTubeQty) : 0,
        collectedCrushedQty: row.hasCollected ? number(row.collectedCrushedQty) : 0,
        note: identity.note || "",
      };
      const url = row.recordId ? `${API_BASE_URL}/saledata/api/module/bag-refill/${row.recordId}` : `${API_BASE_URL}/saledata/api/module/bag-refill`;
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(url, { method: row.recordId ? "PUT" : "POST", headers: headers(), body: JSON.stringify(body) });
        if (!response.ok) { updateRow(row.rowId, "error", (await response.text()) || "บันทึกไม่สำเร็จ"); continue; }
        anySucceeded = true;
        setRows(current => {
          const remaining = current.filter(candidate => candidate.rowId !== row.rowId);
          return remaining.length ? remaining : [{ ...blankRefillRow(), rowId: row.rowId }];
        });
      } catch (e) {
        updateRow(row.rowId, "error", "บันทึกไม่สำเร็จ: " + e.message);
      }
    }
    if (anySucceeded) { setMessage("บันทึกรายการเพิ่มน้ำแข็งแล้ว"); setDraftFrom(null); await reload(); }
    setSaving(false);
  };

  return <>
    <div style={ledgerCard}>
      <div style={formHeader}><div><h3 style={sectionTitle}>เพิ่มรายการเพิ่มน้ำแข็ง</h3></div></div>
      <div style={identityGrid}>
        <label style={label}>วันที่<input type="date" value={businessDate} onChange={event => setBusinessDate(event.target.value)} style={control} /></label>
        <label style={label}>สาย
          <select value={route} onChange={event => setRoute(event.target.value)} style={control}>
            <option value="">-- เลือกสาย --</option>
            {route && !routeOptions.some(option => routeValue(option) === route) && <option value={route}>{route}</option>}
            {routeOptions.map(option => <option key={option.uid} value={routeValue(option)}>{routeValue(option)}</option>)}
          </select>
        </label>
      </div>
      {draftFrom && <div style={draftBanner}>ยกยอดจากวันที่ {draftFrom}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "end", marginBottom: 16 }}>
        <label style={smallLabel}>ไม่ใช่ยอดนี้? คัดลอกจากวันที่...<input type="date" value={copyDate} onChange={event => setCopyDate(event.target.value)} style={control} /></label>
        <button type="button" style={editButton} onClick={copyFromDate}>ดึงมา</button>
      </div>
      <div style={identityGrid}>
        <label style={label}>ทะเบียนรถ (ถ้ามี)<input list="vehicle-plate-options-refill" value={identity.vehiclePlate || ""} onChange={event => setIdentityField("vehiclePlate", event.target.value)} style={control} placeholder="เช่น 1087" /><datalist id="vehicle-plate-options-refill">{vehiclePlates.map(plate => <option key={plate} value={plate} />)}</datalist></label>
      </div>
      <div style={rowsList}>
        {rows.map((row, index) => <div key={row.rowId} style={tRow}>
          <span style={tRowLabel}>T{index + 1}</span>
          <label style={tRowField}>หลอดใหญ่<input type="number" min="0" value={row.tubeQty} onChange={event => updateRow(row.rowId, "tubeQty", event.target.value)} style={control} /></label>
          <label style={tRowField}>บด<input type="number" min="0" value={row.crushedQty} onChange={event => updateRow(row.rowId, "crushedQty", event.target.value)} style={control} /></label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#334155", paddingBottom: 9 }}>
            <input type="checkbox" checked={row.hasCollected} onChange={event => updateRow(row.rowId, "hasCollected", event.target.checked)} /> เก็บกลับ
          </label>
          {row.hasCollected && <>
            <label style={tRowField}>เก็บกลับ หลอดใหญ่<input type="number" min="0" value={row.collectedTubeQty} onChange={event => updateRow(row.rowId, "collectedTubeQty", event.target.value)} style={control} /></label>
            <label style={tRowField}>เก็บกลับ บด<input type="number" min="0" value={row.collectedCrushedQty} onChange={event => updateRow(row.rowId, "collectedCrushedQty", event.target.value)} style={control} /></label>
          </>}
          {row.recordId && <button type="button" style={rowDeleteButton} onClick={() => deleteFormRow(row)}>ลบแถวนี้</button>}
          {rows.length > 1 && <button type="button" style={rowRemoveButton} onClick={() => discardRow(row.rowId)} title="ลบต๊งนี้">×</button>}
          {row.error && <p style={{ ...error, flexBasis: "100%", margin: 0 }}>{row.error}</p>}
        </div>)}
      </div>
      <label style={{ ...label, marginTop: 16 }}>หมายเหตุ<textarea value={identity.note || ""} onChange={event => setIdentityField("note", event.target.value)} style={{ ...control, minHeight: 56, resize: "vertical" }} /></label>
      <div style={previewCard}><strong>จะบันทึก:</strong> ทะเบียนรถ {identity.vehiclePlate || "-"} · {rows.length} ต๊ง</div>
      <div style={actions}>
        <button type="button" style={editButton} onClick={addRow}>+ เพิ่มต๊ง (T)</button>
      </div>
      <div style={{ ...actions, marginTop: 8 }}>
        <button type="button" style={saveButton} onClick={saveAll} disabled={saving}>{saving ? "กำลังบันทึก..." : "บันทึกทั้งหมด"}</button>
        {rows.some(row => row.recordId) && <button type="button" style={backButton} onClick={cancelEdit}>ยกเลิก</button>}
      </div>
      {message && <p style={messageStyle(message)}>{message}</p>}
    </div>

    <div style={ledgerCard}>
      <h2 style={sectionTitle}>เพิ่มน้ำแข็ง</h2>
      <div style={tableWrap}><table style={ledgerTable}><thead><tr><th style={rowHeader}>เวลา</th><th style={rowHeader}>ทะเบียนรถ</th><th style={numberHeader}>หลอดใหญ่</th><th style={numberHeader}>บด</th><th style={numberHeader}>เก็บกลับระหว่างทาง</th><th style={numberHeader}>จัดการ</th></tr></thead><tbody>
        {entries.map(entry => <tr key={entry.id}>
          <td style={rowHeader}>{formatTime(entry.createdAt)}</td>
          <td style={rowHeader}>{entry.vehiclePlate || "-"}</td>
          <td style={sumCell}>{number(entry.tubeQty)}</td>
          <td style={sumCell}>{number(entry.crushedQty)}</td>
          <td style={sumCell}>{formatPair(entry.collectedTubeQty, entry.collectedCrushedQty)}</td>
          <td style={sumCell}><button type="button" style={editButton} onClick={() => editEntry(entry)}>แก้ไข</button><button type="button" style={deleteButton} onClick={() => removeTableEntry(entry)}>ลบ</button></td>
        </tr>)}
        {!entries.length && <tr><td colSpan="6" style={emptyRow}>ยังไม่มีรายการเพิ่มน้ำแข็ง</td></tr>}
      </tbody></table></div>
    </div>
  </>;
};

const emptyReturnForm = () => ({ returnedTubeQty: "", returnedCrushedQty: "", note: "" });

const ReturnTab = ({ businessDate, setBusinessDate, route, setRoute, routeOptions, headers }) => {
  const [form, setForm] = useState(emptyReturnForm());
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setMessage("");
    if (!route) { setForm(emptyReturnForm()); setEditingId(null); return; }
    let cancelled = false;
    const run = async () => {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-return/by-route?businessDate=${businessDate}&route=${encodeURIComponent(route)}`, { headers: headers() });
      if (cancelled) return;
      if (response.status === 404) { setForm(emptyReturnForm()); setEditingId(null); return; }
      if (!response.ok) { setMessage("ดึงข้อมูลกระสอบกลับไม่สำเร็จ"); return; }
      const data = await response.json();
      setForm({ returnedTubeQty: data.returnedTubeQty ?? "", returnedCrushedQty: data.returnedCrushedQty ?? "", note: data.note || "" });
      setEditingId(data.id);
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate, route]);

  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const submit = async event => {
    event.preventDefault();
    if (!route) { setMessage("กรุณาเลือกสาย"); return; }
    const body = { businessDate, route: route.trim(), sellerUid: uidForRoute(routeOptions, route), returnedTubeQty: number(form.returnedTubeQty), returnedCrushedQty: number(form.returnedCrushedQty), note: form.note || "" };
    const url = editingId ? `${API_BASE_URL}/saledata/api/module/bag-return/${editingId}` : `${API_BASE_URL}/saledata/api/module/bag-return`;
    const response = await fetch(url, { method: editingId ? "PUT" : "POST", headers: headers(), body: JSON.stringify(body) });
    if (!response.ok) { setMessage((await response.text()) || "บันทึกไม่สำเร็จ"); return; }
    if (!editingId) { const saved = await response.json(); setEditingId(saved.id); }
    setMessage(editingId ? "แก้ไขกระสอบกลับแล้ว" : "บันทึกกระสอบกลับแล้ว");
  };

  return <>
    <form onSubmit={submit} style={ledgerCard}>
      <div style={formHeader}><div><h2 style={sectionTitle}>{editingId ? "แก้ไขกระสอบกลับ" : "ลงกระสอบกลับ"}</h2><p style={helper}>บันทึกยอดกระสอบที่กลับมาถึงโรงงานตอนจบกะ</p></div></div>
      <div style={identityGrid}>
        <label style={label}>วันที่<input type="date" value={businessDate} onChange={event => setBusinessDate(event.target.value)} style={control} /></label>
        <label style={label}>สาย
          <select value={route} onChange={event => setRoute(event.target.value)} style={control}>
            <option value="">-- เลือกสาย --</option>
            {route && !routeOptions.some(option => routeValue(option) === route) && <option value={route}>{route}</option>}
            {routeOptions.map(option => <option key={option.uid} value={routeValue(option)}>{routeValue(option)}</option>)}
          </select>
        </label>
      </div>
      <div style={identityGrid}>
        <label style={label}>กลับมา หลอดใหญ่<input type="number" min="0" value={form.returnedTubeQty} onChange={event => set("returnedTubeQty", event.target.value)} style={control} /></label>
        <label style={label}>กลับมา บด<input type="number" min="0" value={form.returnedCrushedQty} onChange={event => set("returnedCrushedQty", event.target.value)} style={control} /></label>
      </div>
      <label style={{ ...label, marginTop: 16 }}>หมายเหตุ<textarea value={form.note || ""} onChange={event => set("note", event.target.value)} style={{ ...control, minHeight: 56, resize: "vertical" }} /></label>
      <div style={previewCard}><strong>จะบันทึก:</strong> กลับมา หลอดใหญ่ {number(form.returnedTubeQty)} · บด {number(form.returnedCrushedQty)}</div>
      <div style={actions}><button style={saveButton}>{editingId ? "บันทึกการแก้ไขกระสอบกลับ (จบกะ)" : "บันทึกกระสอบกลับ (จบกะ)"}</button></div>
      {message && <p style={messageStyle(message)}>{message}</p>}
    </form>
  </>;
};

// "สรุป" — the single review/edit/send hub (2026-08-28 consolidation: LoadOutTab went back to
// pure entry, so everything about looking at/fixing/sending today's data lives here instead).
// Reads back the vehiclePlate now on load-out/refill records (merged server-side, see
// BagLedgerVehicleReportService), across ALL routes/vehicles for the page's businessDate (not
// scoped to the "สาย" filter above, since this report never was route-scoped to begin with).
const SummaryTab = ({ businessDate, setBusinessDate, route, setRoute, routeOptions, headers, active, onEditRecord, dailyNote, setDailyNoteField, saveDailyNote }) => {
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentImageUrl, setSentImageUrl] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryMessage, setSummaryMessage] = useState("");

  const reload = useCallback(async () => {
    setMessage("");
    const reportResponse = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger/vehicle-report?businessDate=${businessDate}`, { headers: headers() });
    setRows(reportResponse.ok ? await reportResponse.json() : []);
    if (!reportResponse.ok) setMessage("ดึงข้อมูลสรุปไม่สำเร็จ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate]);

  const reloadSummary = useCallback(async () => {
    if (!route) { setSummary(null); setSummaryMessage("เลือกสายเพื่อดูสรุป"); return; }
    setSummaryMessage("");
    const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger-summary?businessDate=${businessDate}&route=${encodeURIComponent(route)}`, { headers: headers() });
    if (!response.ok) { setSummary(null); setSummaryMessage("ดึงข้อมูลสรุปไม่สำเร็จ"); return; }
    setSummary(await response.json());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate, route]);

  useEffect(() => {
    if (!active) return;
    reload();
    reloadSummary();
    setSentImageUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reload, reloadSummary]);

  // Same race as LoadOutTab's saveAndSend: the machine-time inputs below save on onBlur, not
  // guaranteed to have landed by the time "ส่งแจ้ง LINE" is clicked — await the current in-state
  // values here first so this tab's send can't go out with a stale/missing footer either.
  const send = async () => {
    setSending(true);
    setMessage("");
    try {
      const noteSave = await saveDailyNote();
      if (!noteSave.ok) { setMessage(noteSave.message); return; }
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger/vehicle-report/send`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ businessDate }),
      });
      if (!response.ok) { setMessage((await response.text()) || "ส่งไม่สำเร็จ"); return; }
      const data = await response.json();
      setSentImageUrl(data.imageUrl);
      setMessage("ส่งแจ้ง LINE แล้ว");
    } catch (e) {
      setMessage("ส่งไม่สำเร็จ: " + e.message);
    } finally {
      setSending(false);
    }
  };

  return <>
    <div style={ledgerCard}>
      <div style={formHeader}><div><h2 style={sectionTitle}>สรุป — ยอดขึ้นน้ำแข็งรายทะเบียนรถ</h2><p style={helper}>รายงานนี้ครอบทุกสาย/ทุกคันของวันที่เลือกด้านบนของหน้า</p></div></div>
      <VehicleReportTable rows={rows} onEditRecord={onEditRecord} />
    </div>

    <div style={ledgerCard}>
      <h2 style={sectionTitle}>สรุปบัญชีกระสอบ</h2>
      <div style={identityGrid}>
        <label style={label}>วันที่<input type="date" value={businessDate} onChange={event => setBusinessDate(event.target.value)} style={control} /></label>
        <label style={label}>สาย
          <select value={route} onChange={event => setRoute(event.target.value)} style={control}>
            <option value="">-- เลือกสาย --</option>
            {route && !routeOptions.some(option => routeValue(option) === route) && <option value={route}>{route}</option>}
            {routeOptions.map(option => <option key={option.uid} value={routeValue(option)}>{routeValue(option)}</option>)}
          </select>
        </label>
      </div>
      {summaryMessage && <p style={messageStyle(summaryMessage)}>{summaryMessage}</p>}
      {summary && <div style={tableWrap}><table style={ledgerTable}><thead><tr><th style={rowHeader}>รายการ</th><th style={numberHeader}>หลอดใหญ่</th><th style={numberHeader}>บด</th></tr></thead><tbody>
        <tr><th style={rowHeader}>ขึ้นน้ำแข็ง</th>{summary.loadOut ? <><td style={sumCell}>{number(summary.loadOut.tubeQty)}</td><td style={sumCell}>{number(summary.loadOut.crushedQty)}</td></> : <td colSpan="2" style={emptyRow}>ยังไม่มีข้อมูล</td>}</tr>
        <tr style={calculatedRow}><th style={rowHeader}>เพิ่มน้ำแข็งรวม</th><td style={sumCell}>{number(summary.refillTotals?.tubeQty)}</td><td style={sumCell}>{number(summary.refillTotals?.crushedQty)}</td></tr>
        <tr><th style={rowHeader}>กระสอบกลับ</th>{summary.return ? <><td style={sumCell}>{number(summary.return.returnedTubeQty)}</td><td style={sumCell}>{number(summary.return.returnedCrushedQty)}</td></> : <td colSpan="2" style={emptyRow}>ยังไม่มีข้อมูล</td>}</tr>
        <tr style={calculatedRow}><th style={rowHeader}>กระสอบกลับรวม <span style={hintText}>(เก็บกลับระหว่างทาง + จบกะ)</span></th><td style={sumCell}>{number(summary.bagsBack?.tubeQty)}</td><td style={sumCell}>{number(summary.bagsBack?.crushedQty)}</td></tr>
        <tr style={calculatedRow}><th style={rowHeader}>ขาย</th><td style={sumCell}>{number(summary.realSold?.tubeQty)}</td><td style={sumCell}>{number(summary.realSold?.crushedQty)}</td></tr>
        <tr><th style={rowHeader}>เติมน้ำแข็ง <span style={hintText}>(เติมถุงที่ละลายให้เต็มก่อนส่ง)</span></th><td style={sumCell}>{number(summary.meltCompensation?.tubeQty)}</td><td style={sumCell}>{number(summary.meltCompensation?.crushedQty)}</td></tr>
        <tr style={calculatedRow}><th style={rowHeader}>น้ำแข็งที่ขายไม่ออก <span style={hintText}>(ขึ้น + เพิ่ม − ขาย − เติม)</span></th><td style={sumCell}>{number(summary.loadOut?.tubeQty) + number(summary.refillTotals?.tubeQty) - number(summary.realSold?.tubeQty) - number(summary.meltCompensation?.tubeQty)}</td><td style={sumCell}>{number(summary.loadOut?.crushedQty) + number(summary.refillTotals?.crushedQty) - number(summary.realSold?.crushedQty) - number(summary.meltCompensation?.crushedQty)}</td></tr>
        <tr style={varianceRow}><th style={rowHeader}>ขาด / เกิน <span style={hintText}>(กระสอบกลับรวม − ขาย)</span></th><td style={sumCell}>{number(summary.variance?.tubeQty)}</td><td style={sumCell}>{number(summary.variance?.crushedQty)}</td></tr>
      </tbody></table></div>}
    </div>

    {summary && <div style={ledgerCard}>
      <h2 style={sectionTitle}>รายการเพิ่มน้ำแข็ง</h2>
      <div style={tableWrap}><table style={ledgerTable}><thead><tr><th style={rowHeader}>เวลา</th><th style={numberHeader}>หลอดใหญ่</th><th style={numberHeader}>บด</th><th style={numberHeader}>เก็บกลับระหว่างทาง</th></tr></thead><tbody>
        {summary.refills.map((entry, index) => <tr key={entry.timestamp ?? index}><td style={rowHeader}>{formatTime(entry.timestamp)}</td><td style={sumCell}>{number(entry.tubeQty)}</td><td style={sumCell}>{number(entry.crushedQty)}</td><td style={sumCell}>{formatPair(entry.collectedTubeQty, entry.collectedCrushedQty)}</td></tr>)}
        {!summary.refills.length && <tr><td colSpan="4" style={emptyRow}>ยังไม่มีรายการเพิ่มน้ำแข็ง</td></tr>}
        <tr style={calculatedRow}><th style={rowHeader}>รวม</th><td style={sumCell}>{number(summary.refillTotals?.tubeQty)}</td><td style={sumCell}>{number(summary.refillTotals?.crushedQty)}</td><td style={sumCell}>{formatPair(summary.refillTotals?.collectedTubeQty, summary.refillTotals?.collectedCrushedQty)}</td></tr>
      </tbody></table></div>
    </div>}

    <div style={ledgerCard}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ color: "#334155", fontSize: 13, fontWeight: 700 }}>หมายเหตุประจำวัน (เวลาเปิด-ปิดเครื่องทำน้ำแข็ง)</span>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "end" }}>
            <span style={{ color: "#64748b", fontSize: 13, fontWeight: 700, paddingBottom: 9 }}>เครื่อง 1</span>
            <label style={label}>เปิด<input type="time" value={dailyNote.machine1PowerOnTime} onChange={event => setDailyNoteField("machine1PowerOnTime", event.target.value)} onBlur={saveDailyNote} style={control} /></label>
            <label style={label}>ปิด<input type="time" value={dailyNote.machine1PowerOffTime} onChange={event => setDailyNoteField("machine1PowerOffTime", event.target.value)} onBlur={saveDailyNote} style={control} /></label>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "end" }}>
            <span style={{ color: "#64748b", fontSize: 13, fontWeight: 700, paddingBottom: 9 }}>เครื่อง 2</span>
            <label style={label}>เปิด<input type="time" value={dailyNote.machine2PowerOnTime} onChange={event => setDailyNoteField("machine2PowerOnTime", event.target.value)} onBlur={saveDailyNote} style={control} /></label>
            <label style={label}>ปิด<input type="time" value={dailyNote.machine2PowerOffTime} onChange={event => setDailyNoteField("machine2PowerOffTime", event.target.value)} onBlur={saveDailyNote} style={control} /></label>
          </div>
        </div>
      </div>

      <div style={{ ...actions, marginTop: 24 }}><button type="button" style={saveButton} onClick={send} disabled={sending}>{sending ? "กำลังส่ง..." : "ส่งแจ้ง LINE"}</button></div>
      {message && <p style={messageStyle(message)}>{message}</p>}
      {sentImageUrl && <div style={{ marginTop: 14 }}>
        <p style={helper}>รูปที่ส่งจริง:</p>
        <img src={sentImageUrl} alt="ยอดขึ้นน้ำแข็งที่ส่ง" style={{ maxWidth: "100%", border: "1px solid #d9e1ea", borderRadius: 6 }} />
      </div>}
    </div>
  </>;
};

const emptyDailyNote = () => ({ machine1PowerOnTime: "", machine1PowerOffTime: "", machine2PowerOnTime: "", machine2PowerOffTime: "", machine1SpecialNote: "", machine2SpecialNote: "", machine1StartsWith: null, machine2StartsWith: null });

const BagLedgerAccounting = () => {
  const navigate = useNavigate();
  const [businessDate, setBusinessDate] = useState(today());
  const [route, setRoute] = useState("");
  const [routeOptions, setRouteOptions] = useState([]);
  const [routeError, setRouteError] = useState("");
  const [activeTab, setActiveTab] = useState("loadOut");
  const [dailyNote, setDailyNote] = useState(emptyDailyNote());
  const [pendingEditRecordId, setPendingEditRecordId] = useState(null);
  const token = () => JSON.parse(localStorage.getItem("user") || "{}").token;
  const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

  useEffect(() => {
    const loadRoutes = async () => {
      setRouteError("");
      try {
        const { response, rows } = await fetchRouteRegistry(token());
        if (!response.ok) {
          setRouteOptions([]);
          const body = await response.json().catch(() => null);
          setRouteError(body?.message || `โหลดรายชื่อสายไม่สำเร็จ (HTTP ${response.status})`);
          return;
        }
        setRouteOptions(rows);
      } catch (_) { setRouteOptions([]); setRouteError("โหลดรายชื่อสายไม่สำเร็จ"); }
    };
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger/daily-note?businessDate=${businessDate}`, { headers: headers() });
      if (cancelled) return;
      if (!response.ok) { setDailyNote(emptyDailyNote()); return; }
      const data = await response.json();
      // Backend serializes LocalTime as "HH:mm:ss" — <input type="time"> wants "HH:mm".
      const hhmm = value => value ? value.slice(0, 5) : "";
      setDailyNote({
        machine1PowerOnTime: hhmm(data.machine1PowerOnTime),
        machine1PowerOffTime: hhmm(data.machine1PowerOffTime),
        machine2PowerOnTime: hhmm(data.machine2PowerOnTime),
        machine2PowerOffTime: hhmm(data.machine2PowerOffTime),
        machine1SpecialNote: data.machine1SpecialNote || "",
        machine2SpecialNote: data.machine2SpecialNote || "",
        machine1StartsWith: data.machine1StartsWith || null,
        machine2StartsWith: data.machine2StartsWith || null,
      });
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDate]);

  const setDailyNoteField = (key, value) => setDailyNote(current => ({ ...current, [key]: value }));

  // Returns {ok, message} rather than throwing — saveAndSend (BagLedgerAccounting.js's brief,
  // 2026-09-28) needs to know whether the machine-time save actually landed before it's safe to
  // send the LINE image, since the send reads this same row back from the DB.
  //
  // overrides lets a caller save a value it just set without waiting a render cycle for
  // `dailyNote` state to catch up — setState is async, so a click handler that calls
  // setDailyNoteField(key, value) then saveDailyNote() in the same tick would otherwise read the
  // stale dailyNote and save the OLD value (the machine-order toggle buttons need this).
  const saveDailyNote = async (overrides = {}) => {
    const note = { ...dailyNote, ...overrides };
    try {
      const response = await fetch(`${API_BASE_URL}/saledata/api/module/bag-ledger/daily-note`, {
        method: "PUT", headers: headers(),
        body: JSON.stringify({
          businessDate,
          machine1PowerOnTime: note.machine1PowerOnTime || null,
          machine1PowerOffTime: note.machine1PowerOffTime || null,
          machine2PowerOnTime: note.machine2PowerOnTime || null,
          machine2PowerOffTime: note.machine2PowerOffTime || null,
          machine1SpecialNote: note.machine1SpecialNote || null,
          machine2SpecialNote: note.machine2SpecialNote || null,
          machine1StartsWith: note.machine1StartsWith || null,
          machine2StartsWith: note.machine2StartsWith || null,
        }),
      });
      if (!response.ok) return { ok: false, message: (await response.text().catch(() => "")) || `บันทึกเวลาเปิด-ปิดเครื่องไม่สำเร็จ (HTTP ${response.status})` };
      return { ok: true };
    } catch (e) {
      return { ok: false, message: "บันทึกเวลาเปิด-ปิดเครื่องไม่สำเร็จ: " + e.message };
    }
  };

  return <div className="btc-container" style={page}>
    <div style={topBar}><button type="button" style={backButton} onClick={() => navigate(-1)}>← กลับ</button><div><h1 style={heading}>บัญชีกระสอบรายวัน</h1><p style={subtitle}>บันทึกแยกตามเหตุการณ์จริง — ขึ้นน้ำแข็ง / เพิ่มน้ำแข็ง / กระสอบกลับ แล้วดูสรุปรวมได้ที่แท็บสุดท้าย</p></div></div>

    <div style={notice}>แท็บขึ้นน้ำแข็งเป็นแบบแม่แบบล็อค — แถว/ทะเบียนรถ/สาย/จำนวนต๊งมาจากแม่แบบ แก้เฉพาะตัวเลขที่ต่างแล้วกดส่งได้เลย (บัญชีที่มีสิทธิ์แก้แม่แบบกดปุ่ม "แก้แม่แบบ" ที่แท็บนั้น) ส่วนแท็บเพิ่มน้ำแข็งยังต้องกรอก "ทะเบียนรถ" เอง — ระบบรวมเป็นยอดขึ้นน้ำแข็งรายทะเบียนรถให้อัตโนมัติ (เลข T นับเฉพาะรอบขึ้นกระสอบ ไม่นับรอบเพิ่มน้ำแข็ง) กดปุ่ม "ส่งแผนเข้า LINE" ที่แท็บขึ้นน้ำแข็งได้เลย (ส่งรูปแผนตามค่าในฟอร์ม ไม่บันทึกลงบัญชีขึ้นกระสอบจริง) ส่วนรูปยอดจริงจาก IMD ดู/ส่งได้ที่แท็บ "สรุป" (ปุ่ม "แก้ไข" ของแต่ละต๊งที่นั่นเปิดโหมด "แก้ไขของจริง" เฉพาะต๊งนั้น)</div>
    {routeError && <p style={error}>{routeError}</p>}

    <div style={tabBar}>{TABS.map(tab => <button key={tab.key} type="button" style={activeTab === tab.key ? tabButtonActive : tabButton} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>)}</div>

    <div style={{ display: activeTab === "loadOut" ? "block" : "none" }}><LoadOutTab businessDate={businessDate} setBusinessDate={setBusinessDate} routeOptions={routeOptions} headers={headers} pendingEditRecordId={pendingEditRecordId} onEditHandled={() => setPendingEditRecordId(null)} dailyNote={dailyNote} setDailyNoteField={setDailyNoteField} saveDailyNote={saveDailyNote} /></div>
    <div style={{ display: activeTab === "refill" ? "block" : "none" }}><RefillTab businessDate={businessDate} setBusinessDate={setBusinessDate} route={route} setRoute={setRoute} routeOptions={routeOptions} headers={headers} /></div>
    <div style={{ display: activeTab === "return" ? "block" : "none" }}><ReturnTab businessDate={businessDate} setBusinessDate={setBusinessDate} route={route} setRoute={setRoute} routeOptions={routeOptions} headers={headers} /></div>
    <div style={{ display: activeTab === "summary" ? "block" : "none" }}><SummaryTab businessDate={businessDate} setBusinessDate={setBusinessDate} route={route} setRoute={setRoute} routeOptions={routeOptions} headers={headers} active={activeTab === "summary"} onEditRecord={editRecordId => { setPendingEditRecordId(editRecordId); setActiveTab("loadOut"); }} dailyNote={dailyNote} setDailyNoteField={setDailyNoteField} saveDailyNote={saveDailyNote} /></div>
  </div>;
};

// Width follows the real window instead of a fixed 1040px card (Vector, 2026-09-22: "ขยายหน่อย
// ... จากจอผม หน้าต่าง เล็กไปหน่อย" — his screenshot showed half the screen sitting empty while
// the ledger table was forced to scroll from the note columns onward). The cap only exists so an
// ultrawide monitor does not stretch note columns past a readable line length.
const page = { width: "100%", maxWidth: 1900, margin: "0 auto", padding: "8px 4px 36px" };
const topBar = { display: "flex", alignItems: "center", gap: 14, marginBottom: 14 };
const heading = { margin: 0, fontSize: 25, color: "#1e293b" };
const subtitle = { margin: "3px 0 0", color: "#64748b", fontSize: 14 };
const notice = { background: "#fff7d6", border: "1px solid #f2d477", color: "#745600", padding: "10px 13px", borderRadius: 7, fontSize: 13, marginBottom: 14 };
const tabBar = { display: "flex", gap: 4, borderBottom: "2px solid #d9e1ea", marginBottom: 18 };
const tabButton = { background: "transparent", border: 0, borderBottom: "3px solid transparent", padding: "10px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14, color: "#64748b", marginBottom: -2 };
const tabButtonActive = { ...tabButton, color: "#d71920", borderBottom: "3px solid #d71920", fontWeight: 700 };
const ledgerCard = { background: "#fff", border: "1px solid #d9e1ea", borderRadius: 8, padding: 18, marginBottom: 18, boxShadow: "0 1px 2px rgba(15,23,42,.04)" };
const templateRowCard = { border: "1px solid #e2e8f0", borderRadius: 6, padding: 12, background: "#f8fafc" };
const formHeader = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, marginBottom: 14 };
const sectionTitle = { margin: 0, color: "#1e293b", fontSize: 18 };
const helper = { margin: "4px 0 0", color: "#64748b", fontSize: 13 };
const identityGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 16 };
const label = { display: "flex", flexDirection: "column", gap: 5, color: "#334155", fontSize: 13, fontWeight: 700 };
const smallLabel = { ...label, fontSize: 12, fontWeight: 600, color: "#64748b" };
const draftBanner = { background: "#eef6ff", border: "1px solid #bfdbfe", color: "#1e3a5f", padding: "8px 12px", borderRadius: 6, fontSize: 13, fontWeight: 600, marginBottom: 10 };
const previewCard = { background: "#f8fafc", border: "1px dashed #cbd5e1", color: "#334155", padding: "8px 12px", borderRadius: 6, fontSize: 13, marginBottom: 10 };
const rowsList = { display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 };
const tRow = { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "end", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 12px" };
const tRowLabel = { fontWeight: 700, color: "#475569", fontSize: 13, minWidth: 28, paddingBottom: 9 };
const tRowField = { ...label, flex: "1 1 120px", minWidth: 100 };
const rowRemoveButton = { background: "transparent", border: 0, color: "#b42318", cursor: "pointer", fontWeight: 700, fontSize: 18, lineHeight: 1, padding: "0 4px" };
const rowDeleteButton = { background: "#fff", color: "#b42318", border: "1px solid #fca5a5", borderRadius: 4, padding: "5px 9px", cursor: "pointer", fontWeight: 700, fontSize: 12 };
const control = { width: "100%", boxSizing: "border-box", padding: "9px 10px", border: "1px solid #cbd5e1", borderRadius: 5, background: "#fff", fontSize: 14, fontWeight: 400 };
const tableWrap = { overflowX: "auto", border: "1px solid #cbd5e1", borderRadius: 6 };
const ledgerTable = { width: "100%", minWidth: 620, borderCollapse: "collapse", fontSize: 14 };
const rowHeader = { textAlign: "left", padding: "11px 12px", borderBottom: "1px solid #dbe3ec", color: "#263548", background: "#f8fafc" };
const numberHeader = { textAlign: "center", padding: "9px 10px", borderBottom: "1px solid #dbe3ec", color: "#fff", background: "#315d7d", minWidth: 115 };
const sumCell = { textAlign: "center", padding: "8px", borderBottom: "1px solid #dbe3ec", color: "#1e293b" };
const calculatedRow = { background: "#eff6ff", fontWeight: 700 };
const varianceRow = { background: "#fff4f2", fontWeight: 700 };
// คำอธิบายสูตรในหัวแถว — เขียนสูตรไว้ตรงที่คนอ่านตัวเลข ไม่ใช่ให้ไปเดาเอง
const hintText = { fontWeight: 400, fontSize: 11, color: "#64748b" };
const tCellButton = { background: "transparent", border: 0, padding: 0, margin: 0, font: "inherit", color: "#1d4ed8", textDecoration: "underline", textUnderlineOffset: 2, cursor: "pointer" };
const actions = { display: "flex", gap: 10, marginTop: 18 };
const saveButton = { background: "#d71920", color: "#fff", border: 0, borderRadius: 5, padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 14 };
const backButton = { background: "#64748b", color: "#fff", border: 0, borderRadius: 5, padding: "9px 12px", cursor: "pointer", fontWeight: 700 };
const editButton = { background: "#fff", color: "#1d4ed8", border: "1px solid #93c5fd", borderRadius: 4, padding: "6px 10px", cursor: "pointer", fontWeight: 700 };
const deleteButton = { background: "#fff", color: "#b42318", border: "1px solid #fca5a5", borderRadius: 4, padding: "6px 10px", cursor: "pointer", fontWeight: 700, marginLeft: 6 };
const error = { color: "#b42318", marginBottom: 0, fontWeight: 600 };
const success = { color: "#087443", marginBottom: 0, fontWeight: 600 };
const emptyRow = { textAlign: "center", color: "#64748b", padding: 20 };
const identityInput = { width: "100%", boxSizing: "border-box", padding: "4px 6px" };
const includeLabel = { display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: 12, fontWeight: 400 };
const editedHint = { color: "#b45309", fontSize: 11, fontWeight: 400 };
const cellInput = { width: 74, textAlign: "center", padding: "6px 4px" };
// ช่อง note ต้องไม่ใช้ความกว้างของช่องตัวเลข (Vector, 2026-09-22: "ระวังเรื่องช่อง note ด้วย อย่าแคบเกินไป") —
// เนื้อหาจริงเป็นข้อความอย่าง "(S3=13), (S5=5)" ไม่ใช่ "28-16" จึงกว้างกว่าและชิดซ้าย ไม่จัดกลาง
const noteInput = { width: 150, textAlign: "left", padding: "6px 8px" };
const cellErrorText = { color: "#b42318", fontSize: 10, marginTop: 2 };
// สาย/ทะเบียนรถ are optional (2026-09-22, Vector) — these read as informational notices, not
// warnings/prohibitions, so they use the same soft-yellow/gray palette as `notice`/`hintText`
// rather than the red error palette.
const warnBanner = { background: "#fff7d6", border: "1px solid #f2d477", color: "#745600", padding: "8px 12px", borderRadius: 6, fontSize: 13, fontWeight: 600, marginBottom: 14 };
const realEditCard = { ...ledgerCard, border: "2px solid #ea580c", background: "#fff7ed" };
const dangerButton = { ...backButton, color: "#b42318", borderColor: "#b42318", marginLeft: "auto" };
const detailsToggle = { background: "transparent", border: 0, color: "#1d4ed8", textDecoration: "underline", cursor: "pointer", fontSize: 12, fontWeight: 700, padding: 0 };
const detailsCell = { background: "#f8fafc", padding: "12px 14px" };

// LoadOutTab's grid scrolls horizontally past ~9 T columns — freezing ลำดับ/ทะเบียนรถ/สาย (via
// position: sticky, left offsets computed from each other's own fixed width below) is what keeps
// a row identifiable while its note is being typed off-screen to the right (Vector, round 3:
// "เลื่อนขวาไปกรอก note แล้วคอลัมน์ซ้ายหายไปด้วย"). Needs an explicit width per column (not just
// minWidth) so every row's sticky offset lines up — table layout is "auto" everywhere else in
// this file, but these 3 columns must be pixel-identical top to bottom for sticky to look right.
// สาย was 130px, which cut the placeholder to "-- เลือกสาย -" (visible in Vector's 2026-09-22
// screenshot); 168 fits the full "-- เลือกสาย --" plus the native select arrow at 13px.
const STICKY_COL_WIDTHS = [48, 96, 168];
const stickyLeft = index => STICKY_COL_WIDTHS.slice(0, index).reduce((sum, w) => sum + w, 0);
const stickyHeaderCell = index => ({ ...rowHeader, position: "sticky", left: stickyLeft(index), zIndex: 3, width: STICKY_COL_WIDTHS[index], minWidth: STICKY_COL_WIDTHS[index] });
// bg is passed in per-row (default/orphan/highlight) — a sticky cell needs its OWN opaque
// background, since the <tr>'s background never shows through an element sitting on its own
// compositing layer over the columns scrolling underneath it.
const stickyBodyCell = (index, bg) => ({ ...rowHeader, position: "sticky", left: stickyLeft(index), zIndex: 2, width: STICKY_COL_WIDTHS[index], minWidth: STICKY_COL_WIDTHS[index], background: bg });

// T and รวม only ever hold a short number pair ("28-16") — the shared numberHeader's 115px
// minWidth (sized for "note T.9") was costing 9 numeric columns ~1,035px of a ~992px viewport
// (Vector, round 3). note columns keep the wider default numberHeader; their real width comes
// from noteInput (150px) regardless of header minWidth.
const numberHeaderNarrow = { ...numberHeader, minWidth: 90 };

const scrollbarCss = `
.bagLedgerScrollX { scrollbar-width: thin; scrollbar-color: #94a3b8 #e2e8f0; }
.bagLedgerScrollX::-webkit-scrollbar { height: 10px; }
.bagLedgerScrollX::-webkit-scrollbar-track { background: #e2e8f0; }
.bagLedgerScrollX::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 6px; }
`;

// Centered, matching where these times sit on Vector's original sheet (VectorDoc/1789982511913.jpg
// — the machine-hours line runs centered across the full width under the table), 2026-09-22:
// "เวลาเปิดปิดเครื่อง ควรอยู่ตรงกลาง".
const machineTimeRow = { display: "flex", gap: 24, flexWrap: "wrap", alignItems: "start", justifyContent: "center", marginTop: 18, paddingTop: 14, borderTop: "1px solid #e2e8f0" };
const machineTimeLabel = { color: "#64748b", fontSize: 12, fontWeight: 700, paddingBottom: 9 };
const machineTimeGroup = { display: "flex", flexDirection: "column", gap: 8, minWidth: 220 };
const machineTimeGroupRow = { display: "flex", gap: 14 };
const machineOrderToggleRow = { display: "flex", gap: 6 };
const machineOrderToggle = { background: "#fff", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: 4, padding: "4px 8px", cursor: "pointer", fontSize: 12, fontWeight: 600 };
const machineOrderToggleActive = { ...machineOrderToggle, background: "#d71920", color: "#fff", borderColor: "#d71920" };

export default BagLedgerAccounting;
